import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { blogData } from '../../mocks/blog';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title and meta tags
    document.title = 'Blog - Bilgisayar ve Teknoloji İpuçları | Es Donanım Bilgisayar';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '2. el bilgisayar alırken dikkat edilmesi gerekenler, bilgisayar bakımı, laptop tamiri, SSD yükseltme ve daha fazlası hakkında faydalı bilgiler. Es Donanım Bilgisayar blog yazıları.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'bilgisayar blog, 2. el bilgisayar ipuçları, laptop bakımı, bilgisayar tamiri rehberi, SSD yükseltme, bilgisayar teknoloji yazıları');
    }
    
    // Set canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/blog`);
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Es Donanım Bilgisayar Blog",
      "description": "Bilgisayar ve teknoloji hakkında faydalı bilgiler",
      "url": `${siteUrl}/blog`,
      "publisher": {
        "@type": "Organization",
        "name": "Es Donanım Bilgisayar",
        "logo": {
          "@type": "ImageObject",
          "url": `${siteUrl}/logo.png`
        }
      },
      "blogPost": blogData.slice(0, 10).map(post => ({
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "datePublished": post.date,
        "author": {
          "@type": "Organization",
          "name": "Es Donanım Bilgisayar"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Es Donanım Bilgisayar"
        }
      }))
    });
    document.head.appendChild(schemaScript);
    
    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  const categories = [
    { id: 'all', name: 'Tümü' },
    { id: 'guide', name: 'Rehber' },
    { id: 'tips', name: 'İpuçları' },
    { id: 'news', name: 'Haberler' },
    { id: 'maintenance', name: 'Bakım' },
  ];

  const filteredBlogs = selectedCategory === 'all' 
    ? blogData 
    : blogData.filter(blog => blog.category === selectedCategory);

  return (
    <>
      <Navbar />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary via-primary-dark to-primary overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-secondary rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
            <span className="text-white font-semibold text-sm">Blog & Faydalı Bilgiler</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 font-heading">
            2. El Bilgisayar ve Teknik Servis Rehberi
          </h1>
          <p className="text-white/90 text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            İkinci el bilgisayar alım satımı, laptop tamiri, bilgisayar bakımı ve teknik servis hakkında bilmeniz gereken her şey
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-12 bg-white border-b border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                  selectedCategory === category.id
                    ? 'bg-secondary text-white shadow-lg'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredBlogs.map((blog) => (
              <article
                key={blog.id}
                className="group bg-white rounded-xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden bg-neutral-100">
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-secondary text-white rounded-full text-xs font-semibold">
                      {blog.categoryName}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center space-x-4 text-sm text-neutral-500 mb-4">
                    <div className="flex items-center space-x-1">
                      <i className="ri-calendar-line"></i>
                      <span>{blog.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span>{blog.readTime}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-primary mb-3 line-clamp-2 group-hover:text-secondary transition-colors">
                    {blog.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-neutral-600 text-sm leading-relaxed mb-6 line-clamp-3">
                    {blog.excerpt}
                  </p>

                  {/* Read More */}
                  <Link
                    to={`/blog/${blog.slug}`}
                    className="inline-flex items-center space-x-2 text-secondary font-semibold text-sm group-hover:translate-x-2 transition-transform cursor-pointer"
                  >
                    <span>Devamını Oku</span>
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {/* No Results */}
          {filteredBlogs.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-file-search-line text-6xl text-neutral-300 mb-4"></i>
              <p className="text-neutral-500 text-lg">Bu kategoride henüz içerik bulunmuyor.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6 font-heading">
            Bilgisayar Sorunlarınız İçin Hemen Bize Ulaşın
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            2. el bilgisayar alım satımı veya teknik servis hizmeti için uzman ekibimizle iletişime geçin
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+903121234567"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-secondary text-white rounded-lg font-semibold hover:bg-secondary/90 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              <span>Hemen Ara</span>
            </a>
            <a
              href="https://wa.me/905XXXXXXXXX"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-8 py-4 bg-whatsapp text-white rounded-lg font-semibold hover:bg-whatsapp/90 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              <span>WhatsApp ile Sor</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BlogPage;